import type { Entity, GroupEntity } from '@backstage/catalog-model';
import type { WithoutIndexSignature } from '@ipen-portal/plugin-common';
import type { ComponentProps } from 'react';

import { isGroupEntity } from '@backstage/catalog-model';
import { useEntity } from '@backstage/plugin-catalog-react';
import { EntityCardBlueprint } from '@backstage/plugin-catalog-react/alpha';
import {
  isBusinessDomainEntity,
  isChapterEntity,
  isDataProductEntity,
  isIPenGroupEntity,
  isOrganisationDsoEntity,
  isOrganisationEntity,
  isPartnerSystemEntity,
  isProductEntity,
  isSolutionEntity,
  isSquadEntity,
} from '@ipen-portal/ipen-entity-model';
import {
  and,
  isSharedMailsEntity,
  isTeamsChannelsEntity,
  not,
  notEmpty,
  or,
} from '@ipen-portal/plugin-common';
import {
  DetailTile,
  useGetEntityByRef,
} from '@ipen-portal/plugin-common-react';
import { KeyRoleList } from '@ipen-portal/plugin-key-roles-react';
import { List } from '@mui/material';
import { tss } from 'tss-react/mui';

import { CardListItemComponent } from './components/CardListItemComponent';
import { CoreProcessesDomain } from './components/CoreProcessesDomain.tsx';
import { SharedMailComponent } from './components/SharedMailComponent';
import { TeamsChannelComponent } from './components/TeamsChannelComponent';

const useStyles = tss.create({
  root: {
    '& div:last-child': {
      paddingRight: 0,
    },
  },
});

type Props<E extends Entity> = Pick<
  ComponentProps<typeof KeyRoleList<E>>,
  'relationalRoles' | 'rolesToInclude'
> & {
  // eslint-disable-next-line @eslint-react/no-unused-props
  column?: 'left' | 'right';
  predicate: (entity: Entity) => entity is E;
  specs?: readonly {
    field: keyof WithoutIndexSignature<E['spec']> & string;
    label: string;
  }[];
  title: string;
};

const createConfig = <E extends Entity>(props: Props<E>): Props<E> => props;

const configs = [
  createConfig({
    column: 'right',
    predicate: isOrganisationEntity,
    rolesToInclude: ['parent'],
    title: 'Team Information',
  }),
  createConfig({
    column: 'left',
    predicate: isOrganisationDsoEntity,
    rolesToInclude: ['parent'],
    specs: [{ field: 'code', label: 'DSO Code' }],
    title: 'DSO Information',
  }),
  createConfig({
    column: 'right',
    predicate: isChapterEntity,
    rolesToInclude: ['parent'],
    title: 'Chapter Team Information',
  }),
  createConfig({
    column: 'right',
    predicate: isPartnerSystemEntity,
    rolesToInclude: ['owner', 'businessOwner'],
    title: 'Partner System Team Information',
  }),

  createConfig({
    column: 'right',
    predicate: isSquadEntity,
    rolesToInclude: ['parent'],
    title: 'Squad Team Information',
  }),

  createConfig({
    column: 'left',
    predicate: isProductEntity,
    rolesToInclude: ['owner', 'businessOwner'],
    title: 'Product Information',
  }),

  createConfig({
    column: 'left',
    predicate: isDataProductEntity,
    rolesToInclude: ['owner'],
    title: 'Data Product Information',
  }),

  createConfig({
    column: 'left',
    predicate: isSolutionEntity,
    relationalRoles: [
      {
        entityPredicate: isSolutionEntity,
        fieldKind: 'Group',
        relationFieldExtractor: entity => entity.spec.owner,
        rolesToInclude: ['digitalLead'],
      },
    ],
    rolesToInclude: ['businessOwner'],
    title: 'Solution Information',
  }),

  // fallback
  createConfig({
    column: 'right',
    predicate: (e): e is GroupEntity =>
      and(
        isGroupEntity,
        not(
          or(
            isOrganisationDsoEntity,
            isOrganisationEntity,
            isSquadEntity,
            isChapterEntity,
          ),
        ),
      )(e),
    rolesToInclude: ['businessOwner'],
    title: 'Team Information',
  }),
] as const;

const TeamInformationCardFromConfig = <E extends Entity>() => {
  const { entity } = useEntity();

  const props = configs.find(({ predicate }) => predicate(entity));

  if (!props) {
    return null;
  }

  return <TeamInformationCardInternal<E> {...(props as unknown as Props<E>)} />;
};

const TeamInformationCardInternal = <E extends Entity>({
  predicate,
  relationalRoles,
  rolesToInclude,
  specs,
  title,
}: Props<E>) => {
  const { entity } = useEntity();
  const { classes } = useStyles();

  const { value: group } = useGetEntityByRef(
    isSolutionEntity(entity) || isProductEntity(entity)
      ? entity.spec.owner
      : undefined,
    {
      fallbackKind: 'Group',
      predicate: isIPenGroupEntity,
    },
  );

  const { value: coreProcess } = useGetEntityByRef(
    isSolutionEntity(entity) ? entity.spec.domain : null,
    { fallbackKind: 'Domain', predicate: isBusinessDomainEntity },
  );

  return (
    <DetailTile
      className={classes.root}
      icon="material-symbols:info-outline"
      title={title}
    >
      <KeyRoleList
        entity={entity}
        relationalRoles={relationalRoles}
        rolesToInclude={rolesToInclude}
      />
      <List>
        {[entity, group].filter(isSharedMailsEntity).map(e => (
          <SharedMailComponent key={e.metadata.name} sharedMail={e} />
        ))}
        {[entity, group].filter(isTeamsChannelsEntity).map(e => (
          <TeamsChannelComponent key={e.metadata.name} teamsChannel={e} />
        ))}
        {predicate?.(entity)
          && specs
            ?.map(({ field, label }) =>
              (entity.spec?.[field]
                ? { label, value: entity.spec[field] }
                : undefined),
            )
            .filter(notEmpty)
            .map(({ label, value }) => (
              <CardListItemComponent
                description={value.toString()}
                key={label}
                title={label}
              />
            ))}
        {coreProcess && <CoreProcessesDomain coreProcess={coreProcess} />}
      </List>
    </DetailTile>
  );
};

export const TeamInformationCardLeft = EntityCardBlueprint.make({
  name: 'team-information-left',
  params: {
    filter: entity =>
      configs.some(
        ({ column, predicate }) => column === 'left' && predicate(entity),
      ),
    loader: async () => <TeamInformationCardFromConfig />,
    type: 'info',
  },
});

export const TeamInformationCardRight = EntityCardBlueprint.make({
  name: 'team-information-right',
  params: {
    filter: entity =>
      configs.some(
        ({ column, predicate }) => column === 'right' && predicate(entity),
      ),
    loader: async () => <TeamInformationCardFromConfig />,
    type: 'content',
  },
});

export const OrganisationTeamInformationCard = EntityCardBlueprint.make({
  name: 'organisation-team-information',
  params: {
    filter: isOrganisationEntity,
    loader: async () => (
      <TeamInformationCardInternal
        predicate={isOrganisationEntity}
        rolesToInclude={['businessOwner']}
        title="Organisation Team Information"
      />
    ),

    type: 'content',
  },
});
